/**
 * AppModal — one canonical modal component used everywhere.
 *
 * Why this exists: every modal in the app must behave the same — top-right ×,
 * tap-on-backdrop dismisses, body scroll inside the card (not the page), and a
 * z-index that sits above any sticky action bar (1001+) so accidental taps on
 * underlying CTAs are impossible.
 *
 * Usage:
 *   <AppModal open={open} onClose={() => setOpen(false)} title="Edit Squad">
 *     <p>Body content...</p>
 *     <Button>Confirm</Button>
 *   </AppModal>
 *
 * Sizes: sm (24rem), md (28rem, default), lg (32rem), xl (42rem)
 * dismissOnBackdrop={false} for confirm-required flows.
 */
import { useEffect } from 'react';

const SIZE_MAP = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export default function AppModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  size = 'md',
  dismissOnBackdrop = true,
  hideCloseButton = false,
  contentClassName = '',
  testId,
}) {
  // ESC key closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-3"
      onClick={dismissOnBackdrop ? onClose : undefined}
      data-testid={testId}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-2xl shadow-xl w-full ${SIZE_MAP[size]} max-h-[85vh] overflow-hidden flex flex-col ${contentClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2 flex-shrink-0">
            <div className="min-w-0">
              {title && (
                <h3 className="font-bold text-lg text-[#2B3A67] truncate">{title}</h3>
              )}
              {subtitle && (
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0"
                aria-label="Close"
                data-testid={testId ? `${testId}-close` : 'modal-close'}
              >×</button>
            )}
          </div>
        )}
        <div className="px-5 pb-5 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}
